/*
 * Applying a document suggestion to a form (task 38.03 §7) — the pure part,
 * with no React and no network of its own, so it is testable on its own.
 *
 * Apply means "take what the document says": every value read off the
 * document replaces what the form holds, and the lines are replaced. What
 * the document does not say — a missing date, a supplier nobody picked, a
 * line without an unambiguous VAT code — is left as it is. The user sees the
 * whole suggestion before pressing Apply, and can edit after.
 *
 * `deps` does the fetching:
 *   getContact(id)         → the contact record
 *   getVatCodes(params)    → { data: [codes], meta: { default } } (GET vatTax)
 *   getCurrencyName(code)  → the currency's display name, or null
 * `context.orgCountry` is the org's country, the last fallback for VAT rules;
 * `context.fileId` the document's file, whose attachment Apply marks applied.
 *
 * Returns { changes, fieldsLogic, report }: the values to merge into the
 * state, the fields whose fieldsLogic must run (as if typed), and what was
 * applied or skipped and why — for the panel and the applied log.
 */

// VAT on a receipt from abroad is foreign VAT: one rule, shared with the classifier.
import { foreignVatCode, isLocalVatCode } from '../../../../common/utils/tax/foreignVatCode.js'

export const isEmpty = (v) => v === undefined || v === null || v === ''

/*
 * A whole day as the form stores dates: local noon, ISO — what DateInput
 * writes. Midnight would read as the previous day in UTC east of Greenwich.
 */
export const dayToIso = (day) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '')) return null
    const [y, m, d] = day.split('-').map(Number)
    return new Date(y, m - 1, d, 12).toISOString()
}

// `a.b.c` read from an object.
const read = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj)

/*
 * The VAT picker's query for a line. A param names a form field, or a fixed
 * value, and may list fallbacks read from { document, org } — the supplier's
 * country falls back to the one printed on the document, then the org's: the
 * country rules only apply when there is one, and a receipt with no foreign
 * address is a domestic purchase.
 */
export const vatParams = (vatConfig, source, fallbackScope) => Object.fromEntries(
    Object.entries(vatConfig.params)
        .map(([key, p]) => {
            let value = p.value ?? source[p.field]
            for (const path of p.or || []) {
                if (!isEmpty(value)) break
                value = read(fallbackScope, path)
            }
            return [key, value]
        })
        .filter(([, v]) => !isEmpty(v))
)

/*
 * The VAT code for a rate from the list the line's own VAT picker would show:
 * the rule's default when it has that rate, else the only code with it. Two
 * codes sharing the rate (standard vs representation) is the user's call.
 */
export const vatCodeForRate = (list, rate) => {
    if (rate === null || rate === undefined || !Array.isArray(list?.data)) return null
    // Never the local-VAT code: its 0 % is not a rate a receipt shows, and
    // foreignVatCode alone picks it.
    const same = list.data.filter(c => typeof c.rate === 'number' && Math.abs(c.rate - rate) < 1e-6 && !isLocalVatCode(c))
    const byDefault = same.find(c => c.id === list.meta?.default?.id)
    if (byDefault) return byDefault
    return same.length === 1 ? same[0] : null
}

/*
 * The line's VAT code: the one chosen in the panel (Jev's, or the user's) when
 * the line's VAT picker lists it at the document's rate — else the rule's
 * choice (vatCodeForRate). A pick the picker would not offer is never written.
 * VAT on a receipt from abroad comes first: the local-VAT code (foreignVatCode),
 * also when the classifier chose it, though its 0 % is not the receipt's rate.
 */
export const vatCodeFor = (list, rate, chosenId, foreign = {}) => {
    if (chosenId && rate !== null && rate !== undefined) {
        const chosen = (list?.data || []).find(c => c.id === chosenId)
        if (chosen && typeof chosen.rate === 'number' && Math.abs(chosen.rate - rate) < 1e-6) return chosen
        // Foreign VAT is 0 % here; the receipt's rate is the other country's.
        if (chosen && isLocalVatCode(chosen)) return chosen
    }
    return foreignVatCode(list, { ...foreign, vatRate: rate }) ?? vatCodeForRate(list, rate)
}

/*
 * `selection` is what the user settled on in the panel:
 *   supplierId    a contact id, or null to leave the supplier alone
 *   titles        per line, the index of the chosen title suggestion
 *   accounts      per line, the chosen account { id, name, number } or null
 *   vat           per line, the chosen VAT code id, or null for the rule's
 */
export const buildApply = async ({ state, extraction, selection, config, deps, context = {} }) => {
    const changes = {}
    const fieldsLogic = []
    const report = { applied: [], skipped: [] }
    const set = (field, value, logic = false) => {
        changes[field] = value
        report.applied.push(field)
        if (logic && config.fieldsLogic.includes(field)) fieldsLogic.push(field)
    }

    // Header: whatever the document says.
    for (const [key, field] of Object.entries(config.header)) {
        const raw = extraction[key]
        if (isEmpty(raw)) continue
        const value = config.dates.includes(key) ? dayToIso(raw) : raw
        if (!isEmpty(value) && value !== state[field]) set(field, value, true)
    }

    const cur = config.currency
    if (extraction.currency && extraction.currency !== state[cur.id]) {
        set(cur.id, extraction.currency, true)
        changes[cur.name] = (await deps.getCurrencyName(extraction.currency)) ?? extraction.currency
        // What the currency picker writes besides the code. The org's own
        // currency is at par, and the rate service has no rate for it; any
        // other currency gets its rate from the currency's fieldsLogic.
        if (context.orgCurrency) {
            changes[cur.base] = context.orgCurrency
            if (extraction.currency === context.orgCurrency) changes[cur.rate] = 1
        }
    }

    // Supplier: the contact's fields, exactly as the supplier picker maps them.
    const sup = config.supplier
    if (selection.supplierId && selection.supplierId !== state[sup.idField]) {
        const contact = await deps.getContact(selection.supplierId)
        if (contact?.id) {
            // A field the contact lacks becomes '' — never null, which the
            // document's schema rejects on these string fields — so the
            // previous supplier's address is still cleared.
            for (const [field, source] of Object.entries(sup.valueMap)) changes[field] = contact[source] ?? ''
            report.applied.push(sup.idField)
        } else {
            report.skipped.push({ field: sup.idField, reason: 'notFound' })
        }
    }

    // Lines: replaced by the document's.
    const L = config.lines
    const source = { ...state, ...changes }
    const fallbackScope = { document: extraction, org: { country: context.orgCountry } }
    const lists = new Map()
    const codesFor = async (params) => {
        const key = JSON.stringify(params)
        if (!lists.has(key)) lists.set(key, await deps.getVatCodes(params))
        return lists.get(key)
    }
    const lines = []
    for (const [i, line] of extraction.lines.entries()) {
        const suggestion = line.suggestions[selection.titles?.[i] ?? 0] ?? line.suggestions[0] ?? {}
        const out = {
            [L.title]: suggestion.title ?? line.text ?? '',
            [L.description]: suggestion.description ?? '',
            [L.quantity]: line.quantity ?? 1,
            [L.gross]: line.gross,
            [L.itemType.field]: L.itemType.value
        }
        const account = selection.accounts?.[i]
        if (account?.id) {
            out[L.account.id] = account.id
            out[L.account.name] = account.name ?? null
            out[L.account.number] = account.number ?? null
        }
        const params = vatParams(config.vatTax, { ...source, ...out }, fallbackScope)
        // Where the supplier is, as the VAT picker reads it, and its VAT number:
        // VAT on a receipt from abroad is foreign VAT.
        const foreign = { supplierCountry: params.recipientLocation, orgCountry: context.orgCountry, taxId: extraction.supplier?.taxId }
        const code = line.vatRate === null || line.vatRate === undefined
            ? null
            : vatCodeFor(await codesFor(params), line.vatRate, selection.vat?.[i], foreign)
        if (code) {
            out[L.tax.id] = code.id
            out[L.tax.name] = code.name
            out[L.tax.rate] = code.rate
        } else {
            // The line and the document's rate: the panel names both, so the
            // user knows which line needs a code and why none was set.
            report.skipped.push({
                field: `${L.array}[${i}].${L.tax.id}`,
                reason: line.vatRate === null || line.vatRate === undefined ? 'noRate' : 'ambiguousVat',
                line: i + 1,
                rate: line.vatRate ?? null
            })
        }
        lines.push(out)
    }
    set(L.array, lines)

    // The document's attachment is marked applied: saved with the record, it
    // keeps a reopened record from offering the suggestion again.
    const mark = config.appliedMark
    const attached = mark && context.fileId ? (state[mark.array] || []) : []
    if (attached.some(a => a[mark.key] === context.fileId)) {
        const at = context.now ?? new Date().toISOString()
        changes[mark.array] = attached.map(a => (a[mark.key] === context.fileId ? { ...a, [mark.field]: at } : a))
    }

    return { changes, fieldsLogic, report }
}
